import { Component } from '@theme/component';

/**
 * <fit-product-title>
 *
 * Opt-in helper for the Complete Your Routine section. When a product title
 * would wrap onto a second line, this shrinks that title's font size just
 * enough to keep it on a single line. Only the title text is affected; titles
 * that already fit are left untouched.
 *
 * Pattern mirrors assets/product-title-truncation.js (Component + ResizeObserver).
 *
 * @typedef {Object} Refs
 * @extends {Component<Refs>}
 */
class FitProductTitle extends Component {
  /** Minimum font size in px; never shrink below this (accessibility floor). */
  #minFontPx = 12;

  connectedCallback() {
    super.connectedCallback();

    const min = this.dataset.minFont;
    if (min) {
      const px = this.#toPx(min);
      if (px > 0) this.#minFontPx = px;
    }

    /** @type {HTMLElement[]} */
    this.titles = Array.from(this.querySelectorAll('[role="heading"]'));

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) this.#fit(/** @type {HTMLElement} */ (entry.target));
      });
      for (const title of this.titles) this.resizeObserver.observe(title);
    } else {
      this.#handleResize = () => this.titles.forEach((title) => this.#fit(title));
      window.addEventListener('resize', this.#handleResize);
    }

    // Initial pass after layout settles.
    requestAnimationFrame(() => this.titles.forEach((title) => this.#fit(title)));
  }

  /**
   * Shrink a single title until it fits on one line.
   * @param {HTMLElement} heading
   */
  #fit(heading) {
    // The text-block wrapper carries the width; force it to fill the card so the
    // measurement box equals the available card width regardless of block settings.
    const wrapper = heading.closest('.text-block');
    if (wrapper) wrapper.style.width = '100%';

    const available = heading.clientWidth;
    if (available === 0) return; // not laid out yet (e.g. off-screen carousel slide)

    // Width-based guard: font shrink only changes height, so a repeat callback with
    // the same available width is our own write echoing back — skip it.
    if (heading.dataset.fitWidth === String(available)) return;
    heading.dataset.fitWidth = String(available);

    heading.style.whiteSpace = 'nowrap';
    heading.style.fontSize = ''; // reset to the block's base size before measuring

    let size = parseFloat(window.getComputedStyle(heading).fontSize);
    if (!size) return;

    let guard = 0;
    while (heading.scrollWidth > available && size > this.#minFontPx && guard < 60) {
      size -= 0.5;
      heading.style.fontSize = `${size}px`;
      guard += 1;
    }
  }

  /**
   * Convert a CSS length (px or rem) to pixels.
   * @param {string} value
   * @returns {number}
   */
  #toPx(value) {
    const trimmed = value.trim();
    if (trimmed.endsWith('rem')) {
      const rootSize = parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
      return parseFloat(trimmed) * rootSize;
    }
    return parseFloat(trimmed);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.#handleResize) window.removeEventListener('resize', this.#handleResize);
  }

  /** @type {(() => void) | undefined} */
  #handleResize;
}

if (!customElements.get('fit-product-title')) {
  customElements.define('fit-product-title', FitProductTitle);
}

export default FitProductTitle;
